import { RPE_RANGE, PARAMETER_OPTIONS } from "../../../constants";
import convertToRiR from "../../../utils/converters/convert-intensity";
import ParameterInput from "../../inputs/parameter-input";
import TimeSelector from "../../inputs/time-selector";
import UnitInput from "../../inputs/unit-input-add-exercise";
import { mapParameter } from "../../other-exercise/CustomParametersSets";
import LoadToggler from "./LoadToggler";
import ModTooltip from "./ModTooltip";

const { RPE, RPEAMRAP, FREEFORM, LOAD } = PARAMETER_OPTIONS;

const AddSetRow = ({
  primary,
  selectedExercise,
  handleNewSetChange,
  isRiR,
  intensityMeasure,
  isAssistant,
  handleUnitChange,
  newSet,
  preferredLoadInput,
  setPreferredLoadInput,
  isNewSetValid,
  handleAddSet,
  handleParameterInputChange,
  activeUser,
  parametersForm,
}) => {
  if (primary === "assistant" && selectedExercise?.parameters) {
    return (
      <div className="add-set-wrapper">
        {selectedExercise.parameters.map((elem) => {
          return (
            <div
              key={`${elem}-add-set`}
              className={`column${
                elem === FREEFORM ? " free-form-col" : " relative"
              }`}
            >
              <label htmlFor={elem.toLowerCase()}>
                {[RPE, RPEAMRAP].includes(elem) ? intensityMeasure : elem}
                {elem === LOAD && preferredLoadInput === "drop" ? " Mod." : ""}
              </label>
              {elem === LOAD && (
                <ModTooltip preferredLoadInput={preferredLoadInput} />
              )}
              <ParameterInput
                parameter={elem}
                value={parametersForm[mapParameter(elem)]}
                onChange={handleParameterInputChange}
                units={activeUser.units}
                tabIndex={"0"}
                id={`${elem}_id_parameter`}
                intensityMeasure={intensityMeasure}
                isRiR={isRiR}
                preferredLoadInput={preferredLoadInput}
                loadDrop={parametersForm.loadDrop}
              />
            </div>
          );
        })}

        {selectedExercise?.parameters?.includes(LOAD) && (
          <LoadToggler
            isAssistant
            preferredLoadInput={preferredLoadInput}
            setPreferredLoadInput={setPreferredLoadInput}
          />
        )}

        <TimeSelector onChange={handleParameterInputChange} />

        <button
          tabIndex={"0"}
          data-button-type="primary"
          className="mt-4"
          disabled={!isNewSetValid}
          onClick={(e) => handleAddSet(e)}
          onKeyUp={(e) => {
            if (e.which === 13) return handleAddSet(e);
          }}
        >
          Add
        </button>
      </div>
    );
  }

  return (
    <div className="add-set-wrapper">
      <div className="column">
        <label htmlFor="exerciseAddSets">Sets</label>
        <input
          tabIndex={"0"}
          id="exerciseAddSets"
          type="number"
          min={1}
          step="1"
          name="sets"
          onChange={(e) => handleNewSetChange(e)}
        />
      </div>

      <div className="column">
        <label htmlFor="exerciseAddReps">Reps</label>
        <input
          tabIndex={"0"}
          id="exerciseAddReps"
          type="text"
          name="reps"
          onChange={(e) => handleNewSetChange(e)}
        />
      </div>

      <div className="column">
        <label htmlFor="exerciseAddRpe">{isRiR ? "RiR" : "RPE / AMRAP"}</label>
        <select
          tabIndex={"0"}
          id="exerciseAddRpe"
          name="rpe"
          defaultValue={""}
          onChange={(e) => handleNewSetChange(e)}
        >
          <option value="">{intensityMeasure}</option>
          {RPE_RANGE.map((i) => (
            <option key={i} value={i + 1}>
              {convertToRiR(i + 1, !isRiR)}
            </option>
          ))}
          <option value="AMRAP">AMRAP</option>
        </select>
      </div>

      <div className="column load relative">
        <label>Load{preferredLoadInput === "drop" ? " Mod." : ""}</label>
        <ModTooltip preferredLoadInput={preferredLoadInput} />
        {isAssistant ? (
          <div className="Load-Selector">
            <div className={preferredLoadInput === "drop" ? "hidden" : ""}>
              <UnitInput
                tabIndex={"0"}
                metric="kg"
                imperial="lb"
                id="exerciseAddWeightLoad"
                className="text-input"
                name="load"
                placeholder="Weight"
                defaultValue={0}
                type="text"
                onProgramEditor={true}
                onInput={(e) => handleNewSetChange(e)}
                units={newSet.units["load"]}
                handleUnitChange={handleUnitChange}
                min={0}
              />
            </div>
            <div
              className={preferredLoadInput === "drop" ? "relative" : "hidden"}
            >
              <input
                tabIndex={"0"}
                type="text"
                name="loadDrop"
                defaultValue={0}
                placeholder="%"
                onInput={(e) => handleNewSetChange(e)}
              />
              <span className="load-suffix">% mod.</span>
            </div>
          </div>
        ) : (
          <div className="Load-Selector-2">
            <div
              className={
                preferredLoadInput === "percent" ? "relative" : "hidden"
              }
            >
              <input
                tabIndex={"0"}
                type="text"
                min={0}
                id="exerciseAddLoad"
                name="percentRepMax"
                defaultValue={0}
                placeholder="%"
                onInput={(e) => handleNewSetChange(e)}
              />
              <span className="load-suffix">%</span>
            </div>
            <div className={preferredLoadInput === "weight" ? "" : "hidden"}>
              <UnitInput
                tabIndex={"0"}
                metric="kg"
                imperial="lb"
                name="load"
                placeholder="Weight"
                defaultValue={0}
                id="exerciseAddWeightLoad"
                type="text"
                onProgramEditor={true}
                onInput={(e) => handleNewSetChange(e)}
                units={newSet.units["load"]}
                handleUnitChange={handleUnitChange}
                min={0}
              />
            </div>
            <div
              className={preferredLoadInput === "drop" ? "relative" : "hidden"}
            >
              <input
                tabIndex={"0"}
                type="text"
                name="loadDrop"
                id="exerciseAddLoadDrop"
                defaultValue={0}
                placeholder="%"
                onInput={(e) => handleNewSetChange(e)}
              />
              <span className="load-suffix">% mod.</span>
            </div>
          </div>
        )}
      </div>

      <LoadToggler
        isAssistant={isAssistant}
        preferredLoadInput={preferredLoadInput}
        setPreferredLoadInput={setPreferredLoadInput}
      />

      <TimeSelector onChange={handleNewSetChange} />

      <button
        tabIndex={"0"}
        data-button-type="primary"
        className="mt-4"
        disabled={!isNewSetValid}
        onClick={(e) => handleAddSet(e)}
        onKeyUp={(e) => {
          if (e.which === 13) return handleAddSet(e);
        }}
      >
        Add
      </button>
    </div>
  );
};

export default AddSetRow;
